/**
 * The one place the dataLayer is written to.
 *
 * Every ecommerce event in the shop goes through `pushEcommerce`. Before this existed
 * the guard below was copy-pasted into six files, and only `purchase` remembered the
 * `{ ecommerce: null }` reset — so a `begin_checkout` fired after an `add_to_cart`
 * inherited that event's `items`, and GA4 reported the wrong basket.
 */

import { Cart } from 'src/components/context';
import { parsePrice } from 'src/utils/price';
import { hasConsent } from 'src/utils/consent';

declare const window: Window & { dataLayer?: Record<string, unknown>[] };

/**
 * GA4's own item field names. New events use these; `view_item` and `add_to_cart`
 * deliberately keep the legacy `id`/`name` shape they have always sent, because the
 * GTM variables reading them cannot be checked from here.
 */
export interface AnalyticsItem {
  item_id: string;
  item_name: string;
  price?: number;
  quantity?: number;
  index?: number;
  item_list_name?: string;
}

/**
 * Pushes one ecommerce event, preceded by the reset GTM needs.
 *
 * GTM merges each `ecommerce` object into the model it already holds, so without the
 * null push the previous event's `items` survive into this one.
 *
 * Nothing here may throw: these calls sit inside checkout's submit handler and the
 * catalogue's render path, and a failure to report must never cost an order.
 */
export const pushEcommerce = (
  event: string,
  ecommerce: Record<string, unknown>
): void => {
  // GTM replays whatever is already queued once it loads, so events raised before
  // the visitor opted in must never be queued at all.
  if (typeof window === 'undefined' || !hasConsent('analytics')) return;

  try {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ecommerce });
  } catch (error) {
    console.error('Could not push analytics event:', error);
  }
};

/**
 * The basket as GA4 items.
 *
 * A line with no product id is dropped rather than reported as `undefined` — the same
 * defensiveness as cartToItems() in src/utils/cart/cartItems.ts, for the same reason:
 * a cart restored from localStorage can be half-formed.
 *
 * `price` is omitted rather than sent as 0 when a product has none, which is the case
 * for the consultation-restricted brands.
 */
export const cartAnalyticsItems = (cart?: Cart | null): AnalyticsItem[] =>
  (cart?.contents?.nodes ?? []).reduce<AnalyticsItem[]>((items, node) => {
    const product = node?.product?.node;

    if (!product?.databaseId) {
      return items;
    }

    const price = parsePrice(product.price);

    items.push({
      item_id: `${product.databaseId}`,
      item_name: product.name,
      ...(price > 0 ? { price } : {}),
      quantity: node.quantity ?? 0,
    });

    return items;
  }, []);

/**
 * What the goods in the basket are worth, shipping excluded.
 *
 * `cart.total` already carries the chosen shipping cost, so reporting it would make
 * the same basket worth more once a delivery method is picked — an increase GA4 would
 * read as a bigger order. The subtraction mirrors the free-shipping threshold checks
 * in CheckoutForm and on the checkout page, which exclude shipping for the same reason.
 */
export const cartGoodsValue = (cart?: Cart | null): number =>
  parsePrice(cart?.total) - parsePrice(cart?.shippingTotal);
